import { Trash2 } from "lucide-react";
import { PageHeading, Section, Steps, Callout, RoleBadge, Diagram, MockField, MockTable, MockButton } from "../shared";

export default function AdminDeletedItemsSection() {
  return (
    <div data-testid="guide-admin-deleted-items">
      <PageHeading
        icon={Trash2}
        title="Deleted Items"
        description="The recycle bin for action items that were deleted. Bring one back to its previous status with a single click, or leave it here as a record."
      />

      <Callout variant="info" title="In plain words">
        When an action item is deleted, it isn't truly erased — it just moves to this recycle bin and disappears from the everyday lists. From here you can search for it and restore it, exactly where it left off, with its full history intact.
      </Callout>

      <Section title="Who can use it">
        <div className="flex items-center gap-2"><RoleBadge role="it_admin" /><span className="text-xs text-white/55">View: <span className="text-white">view_deleted_items</span>. Restore / purge: <span className="text-white">restore_action_item</span> + <span className="text-white">manage_deleted_items</span>.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it when an action item was deleted by mistake, or when you need to find and bring back something that was removed. It's also a safe record showing nothing is ever permanently lost from the screen.
        </p>
      </Section>

      <Section title="Restoring an item">
        <Diagram
          frameLabel="Admin → Deleted Items"
          legend={[
            <>Use the <span className="text-white">search box</span> to find the item by title or origin reference.</>,
            <>Find its row in the deleted list.</>,
            <>Click <span className="text-white">Restore</span> — it returns to its previous status with history intact.</>,
          ]}
        >
          <MockField placeholder="Search by title or origin reference…" marker={1} />
          <MockTable
            columns={["Item", "Deleted by", "Deleted on", ""]}
            rows={[
              [<span className="inline-flex items-center gap-1.5"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">2</span>Q3 portfolio pack</span>, "s.khan", "12 May", <MockButton label="Restore" marker={3} variant="ghost" />],
              ["IC minutes follow-up", "a.rao", "20 May", <MockButton label="Restore" variant="ghost" />],
            ]}
          />
        </Diagram>
        <Steps steps={[
          <>Find the item in the Deleted list. Use the search box to filter by title or origin reference.</>,
          <>Click <span className="text-white">Restore</span> on the row.</>,
          <>The item returns to its previous status with the audit trail intact and a new RESTORED event appended.</>,
        ]} />
      </Section>

      <Section title="What soft-delete does">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li>Sets <span className="text-white">deleted = true</span> and stamps <span className="text-white">deleted_at</span> + <span className="text-white">deleted_by</span> on the action item row.</li>
          <li>Removes it from every list view (Summary, Register, Updates, KPIs) and from the reminder scheduler.</li>
          <li>Preserves the audit trail and all sub-assignments untouched. Nothing is physically deleted from the database.</li>
        </ul>
      </Section>

      <Callout variant="info" title="There is no permanent purge from the UI">
        Deleted items remain in the database indefinitely as a record. To purge a deleted item permanently, take a backup and run the cleanup query manually against the database.
      </Callout>
    </div>
  );
}
